import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnamesBind from 'classnames/bind';
import Icon from '@concur/nui-widgets/lib/Icon/Icon';
import Spinner from '@concur/nui-widgets/lib/Spinner/Spinner';
import Overlay from './_Overlay';
import UserProfileMenu from '../UserProfileMenu/UserProfileMenu';
import lowerCase from '../utils/lowerCase';
import withNuiFormatter from '../utils/withNuiFormatter';
import { getProfileDisplayText } from '../UserProfileMenu/_ProfileIdentifier';

const ProfileMenuItem = (props) => {
    const {
        actingAs,
        avatarSrc,
        classNameMap,
        cssBlock,
        formatter,
        fullName,
        isActive,
        isGov,
        logoutUser,
        onEndSession,
        onStartSession,
        profileMenuItem,
    } = props;

    const classnames = classnamesBind.bind(classNameMap);

    const [expanded, setExpanded] = useState(false);
    const [showSpinner, setShowSpinner] = useState(false);

    const handleStartLoading = () => {
        setShowSpinner(true);
    };

    const handleEndLoading = () => {
        setShowSpinner(false);
    };

    const handleToggleClick = () => {
        setExpanded(!expanded);
    };

    const handleClose = () => {
        setExpanded(false);
    };

    const drawToggle = () => {
        const { allUsers, companyName, otherUserName } = actingAs;
        const isActingForOthers = !!(allUsers || companyName || otherUserName);

        const buttonClassName = classnames(
            `${cssBlock}__anchor`,
            `${cssBlock}__profile`,
            {
                [`${cssBlock}__anchor--active`]: isActive,
                [`${cssBlock}__profile--acting-as`]: isActingForOthers,
            },
        );

        const buttonLabel = expanded
            ? formatter.formattedMessage({ id: 'CoreUI.profile.closeProfileMenu' })
            : formatter.formattedMessage({ id: 'CoreUI.profile.openProfileMenu' });

        const profileDisplayText = getProfileDisplayText({
            actingAs,
            formatter,
            fullName,
            shortNames: true,
        });

        return (
            <button
                aria-expanded={expanded}
                aria-haspopup="dialog"
                className={buttonClassName}
                data-test={profileMenuItem.id ? `menu-${lowerCase(profileMenuItem.id)}` : null}
                onClick={handleToggleClick}
                type="button"
            >
                {isActingForOthers ? (
                    <div className={classnames(`${cssBlock}__profile-label`)}>
                        <div className={classnames(`${cssBlock}__profile-description`)} title={profileDisplayText}>
                            {profileDisplayText}
                        </div>
                        <span className={classnames(`${cssBlock}__screen-reader-only`)}>
                            {buttonLabel}
                        </span>
                    </div>
                ) : (
                    <>
                        <div
                            aria-hidden
                            className={classnames(`${cssBlock}__profile-label`)}
                        >
                            {profileMenuItem.name}
                        </div>
                        <span className={classnames(`${cssBlock}__screen-reader-only`)}>
                            {buttonLabel}
                        </span>
                    </>
                )}
                <div className={classnames(`${cssBlock}__profile-label`)}>
                    <Icon ariaHidden className={classnames(`${cssBlock}__icon`, `${cssBlock}__icon--squash`)} iconName="arrow-1-s" />
                </div>
                {!isActingForOthers && avatarSrc
                    ? (
                        <img
                            alt=""
                            className={classnames(`${cssBlock}__avatar`)}
                            src={avatarSrc}
                        />
                    )
                    : (
                        <Icon
                            ariaHidden
                            className={classnames(`${cssBlock}__icon`)}
                            iconName={isActingForOthers ? 'user-admin' : 'user'}
                            size="lg"
                        />
                    )}
            </button>
        );
    };

    return (
        <>
            <Overlay
                content={(
                    <UserProfileMenu
                        actingAs={actingAs}
                        fullName={fullName}
                        isGov={isGov}
                        logoutUser={logoutUser}
                        onClose={handleClose}
                        onEndLoading={handleEndLoading}
                        onEndSession={onEndSession}
                        onStartLoading={handleStartLoading}
                        onStartSession={onStartSession}
                        profileUrl={profileMenuItem.url}
                    />
                )}
                disableEdgeDetection
                onHide={handleClose}
                placement="bottom-end"
                show={expanded}
                targetClassName={classnames(`${cssBlock}__target`)}
                includeTargetInTabFlow
            >
                {drawToggle()}
            </Overlay>
            <Spinner
                message={formatter.formattedMessage({ id: 'CoreUI.switchingActingAsView' })}
                type="fullScreen"
                visible={showSpinner}
            />
        </>
    );
};

ProfileMenuItem.displayName = 'ProfileMenuItem';

ProfileMenuItem.propTypes = {
    actingAs: PropTypes.shape({
        allUsers: PropTypes.bool,
        companyName: PropTypes.string,
        otherUserName: PropTypes.string,
    }),
    avatarSrc: PropTypes.string,
    cssBlock: PropTypes.string,
    fullName: PropTypes.string,
    isActive: PropTypes.bool,
    isGov: PropTypes.bool,
    logoutUser: PropTypes.func,
    onEndSession: PropTypes.func,
    onStartSession: PropTypes.func,
    profileMenuItem: PropTypes.object,
};

ProfileMenuItem.defaultProps = {
    actingAs: {},
    profileMenuItem: {},
};

export default withNuiFormatter(ProfileMenuItem);
